/**
 * Publishes Kelly's surface properties to every tracking call site beneath it. Mounted by `KellyChatGate`,
 * which is the one place that sits above ALL of them: the account Outlet (the launcher pills in five shells,
 * the nav rail's nudge), the dock, and `KellyChatProvider` — whose own body calls `useChatConversations`, so
 * the send event can only be reached from above that provider, not from inside its children.
 *
 * See surface-context.ts for why this publishes properties rather than an `AnalyticsContext`.
 */

import type { ReactNode } from "react"
import { KellySurfaceProvider } from "./surface-context"
import { useKellyAnalyticsProperties } from "./use-analytics-properties"
import { useTrackDockView } from "./use-track-dock-view"

/**
 * The dock-view watcher, as a zero-DOM child rather than a hook call in the provider body: a consumer of the
 * surface context cannot be the component that publishes it, or it reads what was above it — the empty
 * default — and the dock's own events would ship without the view and route they exist to report.
 */
function DockViewTracker() {
  useTrackDockView()
  return null
}

export function KellyAnalyticsProvider({ children }: { children: ReactNode }) {
  const properties = useKellyAnalyticsProperties()

  return (
    <KellySurfaceProvider value={properties}>
      {/* In the provider, not the dock: the dock only renders for an entitled account, and this must also be
          the single mount — KellyChatGate renders one, on either branch. */}
      <DockViewTracker />
      {children}
    </KellySurfaceProvider>
  )
}
